import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CircleNotch, PaperPlaneTilt } from "@phosphor-icons/react";
import { api, getErrorMessage } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";
import { inputCls, Label } from "./fields";
import { ContactMethodField } from "./ContactMethodField";
import { AttachmentPicker } from "./AttachmentPicker";
import { useAttachments } from "./useAttachments";

// Standalone ticket form (Contact Support page) — the customer writes the issue themselves.
export function SupportTicketForm({ onDone, onCancel }) {
  const { caravan } = useCustomer();
  const [form, setForm] = useState({
    name: caravan?.customer_name || "", chassis_number: caravan?.chassis_number || "", contact_method: "phone", phone: "", email: "", summary: "",
  });
  const att = useAttachments();
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const byPhone = form.contact_method === "phone";
    try {
      const { data } = await api.post("/support-tickets", {
        ...form, phone: byPhone ? form.phone : "", email: byPhone ? "" : form.email, source: "contact", attachments: att.uploadIds,
      });
      toast.success(`Ticket ${data.reference} lodged`);
      onDone(data);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setSending(false); }
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="support-ticket-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Your name</Label><input data-testid="ticket-name-input" value={form.name} onChange={set("name")} className={inputCls} placeholder="Full name" maxLength={80} /></div>
        <div><Label>Chassis number</Label><input data-testid="ticket-chassis-input" value={form.chassis_number} onChange={set("chassis_number")} className={`${inputCls} font-mono tracking-wider uppercase`} placeholder="OTM123456" maxLength={20} /></div>
      </div>
      <ContactMethodField form={form} setForm={setForm} />
      <div>
        <Label>Describe the issue</Label>
        <textarea data-testid="ticket-summary-input" value={form.summary} onChange={set("summary")} rows={5} maxLength={3000}
          className={`${inputCls} resize-y leading-relaxed`} placeholder="What's happening, which equipment it involves, when it started and anything you've already tried." />
      </div>
      <div><Label hint="Optional">Photos or videos</Label><AttachmentPicker att={att} /></div>
      <p className="text-[0.7rem] text-neutral-500 leading-relaxed">
        Technical assistance only. For a warranty claim, please use the <Link to="/warranty" className="text-[#73bf44] hover:underline" data-testid="ticket-warranty-link">Warranty Claim form</Link>.
      </p>
      <div className="flex items-center justify-end gap-2 pt-1">
        {onCancel && <button type="button" onClick={onCancel} data-testid="ticket-cancel-btn" className="text-sm text-neutral-400 hover:text-white px-4 py-2.5 rounded-full transition-colors">Cancel</button>}
        <button type="submit" disabled={sending || att.uploading} data-testid="ticket-submit-btn" className="inline-flex items-center gap-2 bg-[#73bf44] text-[#050505] rounded-full font-heading font-bold px-5 py-2.5 text-sm hover:bg-[#5fa338] transition-colors disabled:opacity-50">
          {sending || att.uploading ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="fill" />}
          {sending ? "Sending…" : att.uploading ? "Uploading files…" : "Send ticket"}
        </button>
      </div>
    </form>
  );
}
